"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.publishDictionarySchema = exports.saveCustomValuesSchema = exports.updateCustomFieldSchema = exports.customFieldSchema = exports.CUSTOM_TEXT_MAX = exports.CUSTOM_FIELD_VISIBILITY_LABELS = exports.CUSTOM_FIELD_VISIBILITIES = exports.CUSTOM_FIELD_TYPE_LABELS = exports.CUSTOM_FIELD_TYPES = exports.CUSTOM_FIELD_MODULE_LABELS = exports.CUSTOM_FIELD_MODULES = exports.FX_RECIPROCAL_FIGURES = exports.fxRateSchema = exports.sourceKeySchema = exports.updateSourceSchema = exports.createSourceSchema = exports.sourceLabelSchema = exports.CAREER_SITE_SOURCE_KEY = exports.SOURCE_KINDS = exports.SETTINGS_QUERY_KEYS = exports.saveOrganisationSettingsSchema = exports.organisationSettingsSchema = exports.DEFAULT_ORGANISATION_SETTINGS = exports.HOLD_DAYS_MAX = exports.HOLD_DAYS_MIN = exports.FILE_LIMIT_MIN_MB = exports.FILE_LIMIT_MAX_MB = exports.ELIMINATE_MODE_LABELS = exports.ELIMINATE_MODES = exports.WHO_SELECTS_LABELS = exports.WHO_SELECTS = exports.NUMBER_FORMAT_LOCALES = exports.NUMBER_FORMAT_LABELS = exports.NUMBER_FORMATS = exports.DATE_FORMAT_LABELS = exports.DATE_FORMATS = void 0;
exports.organisationSettingsOf = organisationSettingsOf;
exports.megabytes = megabytes;
exports.bytesOf = bytesOf;
exports.customSourceKey = customSourceKey;
exports.reciprocalRate = reciprocalRate;
exports.rateDifference = rateDifference;
exports.fxPairKey = fxPairKey;
exports.fxPairsOf = fxPairsOf;
exports.fxInForce = fxInForce;
exports.fxUpcoming = fxUpcoming;
exports.convertMinor = convertMinor;
exports.rateOn = rateOn;
exports.isChoice = isChoice;
exports.customValueProblem = customValueProblem;
exports.customValueText = customValueText;
const zod_1 = require("zod");
const calendars_1 = require("./calendars");
const money_1 = require("./money");
const schemas_1 = require("./schemas");
/**
 * S31 — the remaining admin configuration: ADMIN-15 system settings, ADMIN-08 sources, the FX
 * table, ADMIN-13 custom fields and ADMIN-14 dictionary versions (DECISIONS-LOG S29–S32 Q10,
 * Q18–Q24). Every setting is read at run time, so a change takes effect without a redeploy.
 */
// --- ADMIN-15: organisation settings -------------------------------------------------------------
exports.DATE_FORMATS = ['dd-mm-yyyy', 'dd/mm/yyyy', 'yyyy-mm-dd'];
exports.DATE_FORMAT_LABELS = { 'dd-mm-yyyy': '31-12-2026', 'dd/mm/yyyy': '31/12/2026', 'yyyy-mm-dd': '2026-12-31' };
/** O3 / Q22: `international` groups in thousands (100,000), `south_asian` in lakhs (1,00,000). */
exports.NUMBER_FORMATS = ['international', 'south_asian'];
exports.NUMBER_FORMAT_LABELS = { international: '100,000 — thousands', south_asian: '1,00,000 — lakhs' };
exports.NUMBER_FORMAT_LOCALES = { international: 'en-GB', south_asian: 'en-IN' };
/** S22 Q5 / Q24: who records the final selection on DEC-02. */
exports.WHO_SELECTS = ['either', 'hiring_manager', 'hr_lead'];
exports.WHO_SELECTS_LABELS = { either: 'The hiring manager or HR', hiring_manager: 'The hiring manager only', hr_lead: 'HR only' };
/** S22 Q6 / Q24: what "Eliminate" does on the comparison. */
exports.ELIMINATE_MODES = ['hide', 'reject'];
exports.ELIMINATE_MODE_LABELS = { hide: 'Hide them from the comparison (they stay in the process)', reject: 'Reject them, with the reason and the regret letter' };
exports.FILE_LIMIT_MAX_MB = 10;
exports.FILE_LIMIT_MIN_MB = 1;
exports.HOLD_DAYS_MIN = 7;
exports.HOLD_DAYS_MAX = 180;
const BYTES_PER_MB = 1024 * 1024;
exports.DEFAULT_ORGANISATION_SETTINGS = {
    currencies: ['BDT', 'GBP', 'USD'],
    reportingCurrency: 'BDT',
    defaultTimezone: 'Asia/Dhaka',
    dateFormat: 'dd-mm-yyyy',
    numberFormat: 'international',
    fiscalYearStartMonth: 7,
    cvMaxMb: 10,
    documentMaxMb: 5,
    finalSelection: { whoSelects: 'either', eliminate: 'hide', holdDays: 30 },
    sendTestFromShortlist: false,
};
const currencySchema = zod_1.z.enum(money_1.SUPPORTED_CURRENCIES);
// Review W18: System settings shows these sentences beside the box (the form has no browser bubbles), so each says what to type.
const wholeNumberSchema = zod_1.z.number({ error: 'Enter a whole number.' }).int('Enter a whole number.');
const fileLimitSchema = wholeNumberSchema.min(exports.FILE_LIMIT_MIN_MB, `At least ${exports.FILE_LIMIT_MIN_MB} MB.`).max(exports.FILE_LIMIT_MAX_MB, `At most ${exports.FILE_LIMIT_MAX_MB} MB.`);
const HOLD_DAYS_MESSAGE = `Between ${exports.HOLD_DAYS_MIN} and ${exports.HOLD_DAYS_MAX} days.`;
exports.organisationSettingsSchema = zod_1.z
    .object({
    currencies: zod_1.z.array(currencySchema).min(1, 'Switch on at least one currency.').max(money_1.SUPPORTED_CURRENCIES.length),
    reportingCurrency: currencySchema,
    defaultTimezone: calendars_1.timezoneSchema,
    dateFormat: zod_1.z.enum(exports.DATE_FORMATS),
    numberFormat: zod_1.z.enum(exports.NUMBER_FORMATS),
    fiscalYearStartMonth: zod_1.z.number().int().min(1).max(12),
    cvMaxMb: fileLimitSchema,
    documentMaxMb: fileLimitSchema,
    finalSelection: zod_1.z.object({
        whoSelects: zod_1.z.enum(exports.WHO_SELECTS),
        eliminate: zod_1.z.enum(exports.ELIMINATE_MODES),
        holdDays: wholeNumberSchema.min(exports.HOLD_DAYS_MIN, HOLD_DAYS_MESSAGE).max(exports.HOLD_DAYS_MAX, HOLD_DAYS_MESSAGE),
    }),
    sendTestFromShortlist: zod_1.z.boolean(),
})
    .refine((settings) => settings.currencies.includes(settings.reportingCurrency), { message: 'The reporting currency must be switched on.', path: ['reportingCurrency'] });
exports.saveOrganisationSettingsSchema = zod_1.z.object({ settings: exports.organisationSettingsSchema, version: zod_1.z.number().int().min(0) });
/** Reads a stored value leniently: anything missing or wrong falls back to the default. */
function organisationSettingsOf(value) {
    const merged = typeof value === 'object' && value !== null ? { ...exports.DEFAULT_ORGANISATION_SETTINGS, ...value } : exports.DEFAULT_ORGANISATION_SETTINGS;
    const parsed = exports.organisationSettingsSchema.safeParse(merged);
    return parsed.success ? parsed.data : exports.DEFAULT_ORGANISATION_SETTINGS;
}
function megabytes(bytes) {
    return Math.round(bytes / BYTES_PER_MB);
}
function bytesOf(megabytesValue) {
    return megabytesValue * BYTES_PER_MB;
}
exports.SETTINGS_QUERY_KEYS = [['app-settings'], ['sources'], ['fx-rates'], ['custom-fields'], ['metric-dictionary']];
// --- ADMIN-08: sources -------------------------------------------------------------------------
exports.SOURCE_KINDS = ['built_in', 'custom', 'system'];
/** The source the career site's applications carry: its row shows only while the Career site part is on (#13). */
exports.CAREER_SITE_SOURCE_KEY = 'career_site';
exports.sourceLabelSchema = zod_1.z.string().trim().min(2, 'At least 2 characters.').max(60, 'At most 60 characters.');
exports.createSourceSchema = zod_1.z.object({ label: exports.sourceLabelSchema, isPublishable: zod_1.z.boolean().default(true) });
/** Each part changes only when sent. `isPublishable` is "Jobs can be published to it" (2026-11-02). */
exports.updateSourceSchema = zod_1.z.object({ label: exports.sourceLabelSchema.optional(), isHidden: zod_1.z.boolean().optional(), isPublishable: zod_1.z.boolean().optional(), version: zod_1.z.number().int().min(1) });
/** An added source's stored key: never changes, whatever the label becomes. */
function customSourceKey(label) {
    const slug = label
        .normalize('NFKD')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 40);
    return `src_${slug || 'source'}`;
}
/** Any stored source key a picker may send: a built-in key or `src_…`. */
exports.sourceKeySchema = zod_1.z.string().trim().regex(/^[a-z][a-z0-9_]{1,59}$/, 'Pick a source from the list.');
exports.fxRateSchema = zod_1.z
    .object({
    fromCurrency: currencySchema,
    toCurrency: currencySchema,
    rate: zod_1.z.string().trim().regex(/^\d{1,10}(\.\d{1,8})?$/, 'A positive number, up to 8 decimal places.'),
    effectiveFrom: calendars_1.isoDateSchema,
})
    .refine((rate) => rate.fromCurrency !== rate.toCurrency, { message: 'Pick two different currencies.', path: ['toCurrency'] })
    .refine((rate) => Number(rate.rate) > 0, { message: 'The rate must be more than 0.', path: ['rate'] });
const DECIMAL_TEXT = /^(-?)(\d+)(?:\.(\d+))?$/;
const TEN = 10n;
function exactOf(text) {
    const match = DECIMAL_TEXT.exec(text.trim());
    if (!match)
        return null;
    const [, sign = '', whole = '0', fraction = ''] = match;
    const digits = BigInt(`${whole}${fraction}`);
    return { digits: sign === '-' ? -digits : digits, scale: fraction.length };
}
function textOfExact({ digits, scale }) {
    const isNegative = digits < 0n;
    const raw = (isNegative ? -digits : digits).toString().padStart(scale + 1, '0');
    const whole = raw.slice(0, raw.length - scale);
    const fraction = raw.slice(raw.length - scale).replace(/0+$/, '');
    const text = fraction === '' ? whole : `${whole}.${fraction}`;
    return isNegative && text !== '0' ? `-${text}` : text;
}
function atScale(value, scale) {
    return value.digits * TEN ** BigInt(scale - value.scale);
}
/** How many figures the reverse of a rate is shown with ("So 1 BDT = 0.00816327 USD"). */
exports.FX_RECIPROCAL_FIGURES = 6;
/**
 * 1 ÷ rate, rounded half up to `figures` significant figures, as text. Exact: worked on the digits, so 1 ÷ 122.5 is
 * 0.00816327 every time. Null for anything that is not a positive decimal.
 */
function reciprocalRate(rate, figures = exports.FX_RECIPROCAL_FIGURES) {
    const value = exactOf(rate);
    if (!value || value.digits <= 0n)
        return null;
    // 10^scale ÷ digits, carried far enough past the point to hold `figures` figures and one to round on.
    const extra = figures + value.digits.toString().length + 2;
    const quotient = TEN ** BigInt(value.scale + extra) / value.digits;
    const length = quotient.toString().length;
    if (length <= figures)
        return textOfExact({ digits: quotient, scale: extra });
    const dropped = length - figures;
    const unit = TEN ** BigInt(dropped);
    const rounded = (quotient + unit / 2n) / unit;
    const scale = extra - dropped;
    return scale >= 0 ? textOfExact({ digits: rounded, scale }) : textOfExact({ digits: rounded * TEN ** BigInt(-scale), scale: 0 });
}
/** `newer − older`, exactly, as text ("0.7", "-1.25", "0"). Null if either is not a decimal. */
function rateDifference(newer, older) {
    const a = exactOf(newer);
    const b = exactOf(older);
    if (!a || !b)
        return null;
    const scale = Math.max(a.scale, b.scale);
    return textOfExact({ digits: atScale(a, scale) - atScale(b, scale), scale });
}
function fxPairKey(a, b) {
    return [a, b].sort().join('|');
}
/**
 * The rates grouped into pairs. A pair reads into the reporting currency when it has it ("1 USD = 122.5 BDT"), else the
 * way its newest rate was typed. Pairs with the reporting currency come first, then by name.
 */
function fxPairsOf(rates, reportingCurrency) {
    const groups = new Map();
    for (const rate of rates) {
        const key = fxPairKey(rate.fromCurrency, rate.toCurrency);
        groups.set(key, [...(groups.get(key) ?? []), rate]);
    }
    const pairs = [...groups.entries()].flatMap(([key, group]) => {
        const newest = [...group].sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom))[0];
        if (!newest)
            return [];
        const hasReporting = newest.fromCurrency === reportingCurrency || newest.toCurrency === reportingCurrency;
        const quote = hasReporting ? reportingCurrency : newest.toCurrency;
        const base = quote === newest.toCurrency ? newest.fromCurrency : newest.toCurrency;
        const read = group.map((stored) => {
            const isReversed = stored.fromCurrency !== base;
            return { stored, rate: isReversed ? (reciprocalRate(stored.rate) ?? stored.rate) : stored.rate, effectiveFrom: stored.effectiveFrom, isReversed };
        });
        read.sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom) || Number(a.isReversed) - Number(b.isReversed));
        return [{ key, base, quote, rates: read }];
    });
    const hasReporting = (pair) => pair.quote === reportingCurrency;
    return pairs.sort((a, b) => Number(hasReporting(b)) - Number(hasReporting(a)) || `${a.base}${a.quote}`.localeCompare(`${b.base}${b.quote}`));
}
/** The rate in force on a day (`yyyy-mm-dd`): the newest on or before it. Null before the pair's first rate. */
function fxInForce(pair, isoDay) {
    return pair.rates.find((rate) => rate.effectiveFrom <= isoDay) ?? null;
}
/** Rates that start after the day, soonest first. */
function fxUpcoming(pair, isoDay) {
    return pair.rates.filter((rate) => rate.effectiveFrom > isoDay).reverse();
}
/** Q21: an amount in another currency at one rate, rounded to the target's minor unit. Display only. */
function convertMinor(amountMinor, from, to, rate) {
    return (0, money_1.toMinorUnits)((0, money_1.toMajorUnits)(amountMinor, from) * Number(rate), to);
}
/**
 * The rate in force on a date: the newest `effectiveFrom` on or before it, for the pair or its
 * inverse (1 ÷ rate). Null when there is none — the amount then stays in its own currency.
 */
function rateOn(rates, from, to, isoDay) {
    if (from === to)
        return '1';
    const usable = rates.filter((rate) => rate.effectiveFrom <= isoDay);
    const direct = usable.filter((rate) => rate.fromCurrency === from && rate.toCurrency === to).sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom))[0];
    const inverse = usable.filter((rate) => rate.fromCurrency === to && rate.toCurrency === from).sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom))[0];
    if (direct && (!inverse || direct.effectiveFrom >= inverse.effectiveFrom))
        return direct.rate;
    if (inverse)
        return String(1 / Number(inverse.rate));
    return null;
}
// --- ADMIN-13: custom fields -------------------------------------------------------------------
exports.CUSTOM_FIELD_MODULES = ['candidate', 'job_opening', 'requisition'];
exports.CUSTOM_FIELD_MODULE_LABELS = { candidate: 'Candidates', job_opening: 'Jobs', requisition: 'Requisitions' };
exports.CUSTOM_FIELD_TYPES = ['short_text', 'long_text', 'number', 'date', 'yes_no', 'one_choice', 'many_choices'];
exports.CUSTOM_FIELD_TYPE_LABELS = {
    short_text: 'Short text',
    long_text: 'Long text',
    number: 'Number',
    date: 'Date',
    yes_no: 'Yes or no',
    one_choice: 'One choice',
    many_choices: 'Several choices',
};
exports.CUSTOM_FIELD_VISIBILITIES = ['all_staff', 'hr_and_recruiters', 'hr_lead'];
exports.CUSTOM_FIELD_VISIBILITY_LABELS = { all_staff: 'Everyone on staff', hr_and_recruiters: 'HR and recruiters', hr_lead: 'HR only' };
exports.CUSTOM_TEXT_MAX = 2000;
/** The editable parts, without defaults — an edit changes only what it names. */
const customFieldEditable = {
    label: zod_1.z.string().trim().min(2, 'At least 2 characters.').max(80),
    options: zod_1.z.array(zod_1.z.string().trim().min(1).max(80)).max(50),
    isRequired: zod_1.z.boolean(),
    minNumber: zod_1.z.number().int().nullable(),
    maxNumber: zod_1.z.number().int().nullable(),
    maxLength: zod_1.z.number().int().min(1).max(exports.CUSTOM_TEXT_MAX).nullable(),
    visibility: zod_1.z.enum(exports.CUSTOM_FIELD_VISIBILITIES),
    helpText: zod_1.z.string().trim().max(200).nullable(),
};
const customFieldBase = zod_1.z.object({
    ...customFieldEditable,
    module: zod_1.z.enum(exports.CUSTOM_FIELD_MODULES),
    fieldType: zod_1.z.enum(exports.CUSTOM_FIELD_TYPES),
    options: customFieldEditable.options.default([]),
    isRequired: customFieldEditable.isRequired.default(false),
    minNumber: customFieldEditable.minNumber.default(null),
    maxNumber: customFieldEditable.maxNumber.default(null),
    maxLength: customFieldEditable.maxLength.default(null),
    visibility: customFieldEditable.visibility.default('all_staff'),
    helpText: customFieldEditable.helpText.default(null),
});
exports.customFieldSchema = customFieldBase.refine((field) => !isChoice(field.fieldType) || field.options.length >= 2, { message: 'Give at least two choices.', path: ['options'] });
exports.updateCustomFieldSchema = zod_1.z.object(customFieldEditable).partial().extend({ isArchived: zod_1.z.boolean().optional(), sortOrder: zod_1.z.number().int().optional(), version: zod_1.z.number().int().min(1) });
exports.saveCustomValuesSchema = zod_1.z.object({
    values: zod_1.z.record(schemas_1.uuidSchema, zod_1.z.union([zod_1.z.string().max(exports.CUSTOM_TEXT_MAX), zod_1.z.number(), zod_1.z.boolean(), zod_1.z.array(zod_1.z.string().max(80)).max(50), zod_1.z.null()])),
});
function isChoice(type) {
    return type === 'one_choice' || type === 'many_choices';
}
/** The field's own rules on one value; `null` = valid. Empty is checked against `isRequired`. */
function customValueProblem(field, value) {
    const isEmpty = value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
    if (isEmpty)
        return field.isRequired ? `${field.label} is required.` : null;
    switch (field.fieldType) {
        case 'short_text':
        case 'long_text': {
            if (typeof value !== 'string')
                return `${field.label} must be text.`;
            const max = field.maxLength ?? (field.fieldType === 'short_text' ? 200 : exports.CUSTOM_TEXT_MAX);
            return value.length > max ? `${field.label}: at most ${max} characters.` : null;
        }
        case 'number': {
            if (typeof value !== 'number' || !Number.isFinite(value))
                return `${field.label} must be a number.`;
            if (field.minNumber !== null && value < field.minNumber)
                return `${field.label}: at least ${field.minNumber}.`;
            if (field.maxNumber !== null && value > field.maxNumber)
                return `${field.label}: at most ${field.maxNumber}.`;
            return null;
        }
        case 'date':
            return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? null : `${field.label} must be a date.`;
        case 'yes_no':
            return typeof value === 'boolean' ? null : `${field.label} must be yes or no.`;
        case 'one_choice':
            return typeof value === 'string' && field.options.includes(value) ? null : `${field.label}: pick one of the choices.`;
        case 'many_choices':
            return Array.isArray(value) && value.every((item) => typeof item === 'string' && field.options.includes(item)) ? null : `${field.label}: pick from the choices.`;
    }
}
/** A value as words, for search and exports. */
function customValueText(value) {
    if (value === null)
        return '';
    if (typeof value === 'boolean')
        return value ? 'Yes' : 'No';
    if (Array.isArray(value))
        return value.join(', ');
    return String(value);
}
exports.publishDictionarySchema = zod_1.z.object({
    note: zod_1.z.string().trim().min(3, 'Say what changed.').max(500),
    definitions: zod_1.z
        .array(zod_1.z.object({
        key: zod_1.z.string().trim().min(1),
        name: zod_1.z.string().trim().min(2).max(120),
        definition: zod_1.z.string().trim().min(10, 'A definition needs at least a sentence.').max(2000),
        exclusions: zod_1.z.string().trim().max(1000),
    }))
        .min(1),
    /** The version being edited — a stale edit is refused (S7). */
    baseVersion: zod_1.z.number().int().min(1),
});
//# sourceMappingURL=admin-config.js.map